import React, {useCallback, useMemo, useState} from "react";
import {Box, Card, CardContent, CardMedia, Tabs, Typography} from "@mui/material";
import ReactECharts from "echarts-for-react";
import PageLayout from "../components/layout/PageLayout";
import ReportFilters from "../components/reports/ReportFilters";
import barChartPreview from "../assets/reports/bar-chart.svg";
import {StyledTab} from "../components/utils/StyleConstants";
import {useTranslation} from "react-i18next";

const formatPeriodLabel = (period, fallback) => {
    if (!period?.dateFrom || !period?.dateTo) {
        return fallback;
    }
    const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
    });
    const fromLabel = formatter.format(new Date(period.dateFrom));
    const toLabel = formatter.format(new Date(period.dateTo));
    return `${fromLabel} - ${toLabel}`;
};

const flattenLeafCategories = (nodes = [], path = []) => {
    const result = [];
    nodes.forEach((node) => {
        const nextPath = [...path, node.name];
        const children = node.children || [];
        if (!children.length) {
            result.push({
                label: nextPath.join(" / "),
                values: node.values || []
            });
        } else {
            result.push(...flattenLeafCategories(children, nextPath));
        }
    });
    return result;
};

const ReportsCategoriesBarChartPage = () => {
    const {t} = useTranslation();
    const [reportType, setReportType] = useState("EXPENSE");
    const [reportResponse, setReportResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const handleApply = useCallback(async (request) => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await fetch("/api/reports/tree", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(request)
            });
            if (!response.ok) {
                throw new Error(`Failed to load report (${response.status})`);
            }
            const json = await response.json();
            setReportResponse(json);
        } catch (error) {
            console.log(error);
            setLoadError(error?.message || t("reports.messages.failedToLoadReport"));
            setReportResponse(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleReset = useCallback(() => {
        setReportResponse(null);
        setLoadError(null);
    }, []);

    const chartOption = useMemo(() => {
        const categoriesTree = reportResponse?.categories || [];
        const periods = reportResponse?.periods || [];
        if (!categoriesTree.length || !periods.length) {
            return null;
        }
        const flat = flattenLeafCategories(categoriesTree);
        if (!flat.length) {
            return null;
        }
        const labels = flat.map((item) => item.label);
        const series = periods.map((period, periodIndex) => ({
            name: formatPeriodLabel(period, `Period ${periodIndex + 1}`),
            type: "bar",
            data: flat.map((item) => Math.abs(Number(item.values?.[periodIndex] || 0)))
        }));
        return {
            tooltip: {
                trigger: "axis",
                axisPointer: {type: "shadow"}
            },
            legend: {type: "scroll", bottom: 0},
            grid: {left: 40, right: 20, top: 40, bottom: 120},
            xAxis: {
                type: "category",
                data: labels,
                axisLabel: {interval: 0, rotate: 30}
            },
            yAxis: {type: "value"},
            series
        };
    }, [reportResponse]);

    return (
        <PageLayout
            title={t("pages.reports.categories.barChartTitle")}
            tabs={
                <Tabs
                    value={reportType}
                    onChange={(_, newValue) => setReportType(newValue)}
                    aria-label={t("common.labels.reportType")}
                    variant="fullWidth"
                    textColor="inherit"
                    TabIndicatorProps={{
                        style: {
                            color: "white",
                            background: "white"
                        }
                    }}
                >
                    <StyledTab label={t("common.transactionType.expense")} value="EXPENSE" />
                    <StyledTab label={t("common.transactionType.income")} value="INCOME" />
                </Tabs>
            }
        >
            <CardContent sx={{minHeight: 200, maxHeight: "90%", overflowY: "auto"}}>
                <ReportFilters
                    reportType={reportType}
                    onReportTypeChange={setReportType}
                    showTypeTabs={false}
                    onApply={handleApply}
                    onReset={handleReset}
                />
                {isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loading")}
                    </Typography>
                )}
                {loadError && (
                    <Typography variant="body2" color="error" sx={{mb: 2}}>
                        {loadError}
                    </Typography>
                )}
                <Card sx={{maxWidth: 1100, margin: "0 auto"}}>
                    {chartOption ? (
                        <Box sx={{height: 520, width: "100%"}}>
                            <ReactECharts option={chartOption} style={{height: "100%", width: "100%"}} />
                        </Box>
                    ) : (
                        <CardMedia
                            component="img"
                            image={barChartPreview}
                            alt={t("pages.reports.categories.cards.bar.alt")}
                        />
                    )}
                </Card>
            </CardContent>
        </PageLayout>
    );
};

export default ReportsCategoriesBarChartPage;
